/**
 * Sphere-vs-triangle and ray-vs-triangle primitives for the arena mesh.
 *
 * The sphere test is the two-stage method quoted in docs/PHYSICS_REFERENCE.md
 * §2: project the sphere centre onto the triangle's plane and read off the
 * barycentric weights; if the projection falls outside the triangle then the
 * closest point is on the boundary, so fall back to the minimum over the three
 * edge segments. Collision when `distance <= radius`.
 *
 * Contact normal is the FACE normal `normalize(cross(e3, e1))`, exactly as the
 * reference specifies — not the direction from the closest point to the sphere
 * centre. The two agree on face contacts and differ on edge/vertex contacts.
 * We keep the reference's choice because the arena mesh has no sharp convex
 * edges the ball can land on (every seam is filleted and tessellated), so the
 * worst-case disagreement is one facet's turn angle.
 *
 * Every routine has a flat-array variant taking `Float64Array` triangle soup
 * (9 floats per triangle) plus an offset. The `Vec3` variants delegate to them,
 * so there is exactly one implementation of each test — the API surface used by
 * tests and the one used by the BVH hot loop cannot drift apart.
 */

import { cross, normalize, sub, vec3, type Mat3, type Vec3 } from './math';
import type { Contact } from './surface';

export interface Triangle {
  a: Vec3;
  b: Vec3;
  c: Vec3;
}

/** Output of the flat closest-point query. Mutated in place to avoid garbage. */
export interface ClosestPoint {
  x: number;
  y: number;
  z: number;
  /** Squared distance from the query point to (x, y, z). */
  d2: number;
}

export const closestPoint = (): ClosestPoint => ({ x: 0, y: 0, z: 0, d2: 0 });

// ---------------------------------------------------------------------------
// Normals
// ---------------------------------------------------------------------------

/**
 * Unnormalized area vector, `cross(e3, e1)` with `e1 = b - a`, `e3 = a - c`.
 * Its length is twice the triangle's area. Algebraically identical to the
 * familiar `cross(b - a, c - a)`; we write the reference's form so that the
 * transcription can be checked against docs/PHYSICS_REFERENCE.md §2 by eye.
 */
export const triangleAreaVector = (a: Vec3, b: Vec3, c: Vec3): Vec3 =>
  cross(sub(a, c), sub(b, a));

export const triangleNormal = (a: Vec3, b: Vec3, c: Vec3): Vec3 =>
  normalize(triangleAreaVector(a, b, c));

// ---------------------------------------------------------------------------
// Closest point
// ---------------------------------------------------------------------------

/** Closest point on segment [a, b] to p. */
export const closestPointOnSegment = (p: Vec3, a: Vec3, b: Vec3): Vec3 => {
  const ex = b.x - a.x;
  const ey = b.y - a.y;
  const ez = b.z - a.z;
  const ee = ex * ex + ey * ey + ez * ez;
  if (ee === 0) return vec3(a.x, a.y, a.z);
  let t = ((p.x - a.x) * ex + (p.y - a.y) * ey + (p.z - a.z) * ez) / ee;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return vec3(a.x + ex * t, a.y + ey * t, a.z + ez * t);
};

/** Scratch for the three-edge fallback; the module is single-threaded. */
const _seg: ClosestPoint = closestPoint();

const segClosestFlat = (
  px: number, py: number, pz: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  out: ClosestPoint
): void => {
  const ex = bx - ax;
  const ey = by - ay;
  const ez = bz - az;
  const ee = ex * ex + ey * ey + ez * ez;
  let t = ee === 0 ? 0 : ((px - ax) * ex + (py - ay) * ey + (pz - az) * ez) / ee;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const qx = ax + ex * t;
  const qy = ay + ey * t;
  const qz = az + ez * t;
  const dx = px - qx;
  const dy = py - qy;
  const dz = pz - qz;
  out.x = qx;
  out.y = qy;
  out.z = qz;
  out.d2 = dx * dx + dy * dy + dz * dz;
};

/**
 * Closest point on triangle `tris[o .. o+8]` to (px, py, pz), written into
 * `out` together with the squared distance.
 *
 * Stage 1 is the barycentric projection; stage 2 is the edge fallback. The
 * barycentric weights are computed as triple products against the unnormalized
 * area vector, so the inside test needs no division at all — only the plane
 * projection divides, and only when the triangle is non-degenerate.
 */
export const closestPointOnTriangleFlat = (
  px: number, py: number, pz: number,
  tris: Float64Array, o: number,
  out: ClosestPoint
): void => {
  const ax = tris[o];
  const ay = tris[o + 1];
  const az = tris[o + 2];
  const bx = tris[o + 3];
  const by = tris[o + 4];
  const bz = tris[o + 5];
  const cx = tris[o + 6];
  const cy = tris[o + 7];
  const cz = tris[o + 8];

  const e1x = bx - ax;
  const e1y = by - ay;
  const e1z = bz - az;
  const e2x = cx - bx;
  const e2y = cy - by;
  const e2z = cz - bz;
  const e3x = ax - cx;
  const e3y = ay - cy;
  const e3z = az - cz;

  // n = cross(e3, e1)
  const nx = e3y * e1z - e3z * e1y;
  const ny = e3z * e1x - e3x * e1z;
  const nz = e3x * e1y - e3y * e1x;
  const nn = nx * nx + ny * ny + nz * nz;

  if (nn > 0) {
    const d = ((px - ax) * nx + (py - ay) * ny + (pz - az) * nz) / nn;
    const qx = px - nx * d;
    const qy = py - ny * d;
    const qz = pz - nz * d;

    // Barycentric weights as signed sub-triangle area vectors dotted with n.
    const pbx = qx - bx;
    const pby = qy - by;
    const pbz = qz - bz;
    const wA = (e2y * pbz - e2z * pby) * nx + (e2z * pbx - e2x * pbz) * ny + (e2x * pby - e2y * pbx) * nz;

    const pcx = qx - cx;
    const pcy = qy - cy;
    const pcz = qz - cz;
    const wB = (e3y * pcz - e3z * pcy) * nx + (e3z * pcx - e3x * pcz) * ny + (e3x * pcy - e3y * pcx) * nz;

    const pax = qx - ax;
    const pay = qy - ay;
    const paz = qz - az;
    const wC = (e1y * paz - e1z * pay) * nx + (e1z * pax - e1x * paz) * ny + (e1x * pay - e1y * pax) * nz;

    if (wA >= 0 && wB >= 0 && wC >= 0) {
      out.x = qx;
      out.y = qy;
      out.z = qz;
      const dx = px - qx;
      const dy = py - qy;
      const dz = pz - qz;
      out.d2 = dx * dx + dy * dy + dz * dz;
      return;
    }
  }

  segClosestFlat(px, py, pz, ax, ay, az, bx, by, bz, out);
  segClosestFlat(px, py, pz, bx, by, bz, cx, cy, cz, _seg);
  if (_seg.d2 < out.d2) {
    out.x = _seg.x;
    out.y = _seg.y;
    out.z = _seg.z;
    out.d2 = _seg.d2;
  }
  segClosestFlat(px, py, pz, cx, cy, cz, ax, ay, az, _seg);
  if (_seg.d2 < out.d2) {
    out.x = _seg.x;
    out.y = _seg.y;
    out.z = _seg.z;
    out.d2 = _seg.d2;
  }
};

const _tri = new Float64Array(9);
const _out: ClosestPoint = closestPoint();

const loadTri = (a: Vec3, b: Vec3, c: Vec3): void => {
  _tri[0] = a.x; _tri[1] = a.y; _tri[2] = a.z;
  _tri[3] = b.x; _tri[4] = b.y; _tri[5] = b.z;
  _tri[6] = c.x; _tri[7] = c.y; _tri[8] = c.z;
};

export const closestPointOnTriangle = (p: Vec3, a: Vec3, b: Vec3, c: Vec3): Vec3 => {
  loadTri(a, b, c);
  closestPointOnTriangleFlat(p.x, p.y, p.z, _tri, 0, _out);
  return vec3(_out.x, _out.y, _out.z);
};

/** Distance from `p` to the triangle (0 when `p` lies on it). */
export const distanceToTriangle = (p: Vec3, a: Vec3, b: Vec3, c: Vec3): number => {
  loadTri(a, b, c);
  closestPointOnTriangleFlat(p.x, p.y, p.z, _tri, 0, _out);
  return Math.sqrt(_out.d2);
};

/**
 * Sphere-vs-triangle contact, or null when they do not overlap.
 * Touching exactly (`distance === radius`) counts as a contact of depth 0,
 * matching the reference's `distance <= radius`.
 */
export const sphereTriangle = (
  center: Vec3,
  radius: number,
  a: Vec3,
  b: Vec3,
  c: Vec3
): Contact | null => {
  loadTri(a, b, c);
  closestPointOnTriangleFlat(center.x, center.y, center.z, _tri, 0, _out);
  if (_out.d2 > radius * radius) return null;
  return {
    point: vec3(_out.x, _out.y, _out.z),
    normal: triangleNormal(a, b, c),
    depth: radius - Math.sqrt(_out.d2),
  };
};

// ---------------------------------------------------------------------------
// Oriented box vs triangle
// ---------------------------------------------------------------------------

/**
 * Result of one box-vs-triangle query. Mutated in place; there is one of these
 * per caller, not one per triangle.
 */
export interface OBBTriangleHit {
  /** Unit face normal of the triangle, WORLD space, pointing out of the surface. */
  nx: number;
  ny: number;
  nz: number;
  /** How far the BOX must move along that normal to stop overlapping. */
  depth: number;
  /** Contact point, WORLD space. See the note on support centroids below. */
  px: number;
  py: number;
  pz: number;
}

export const obbTriangleHit = (): OBBTriangleHit => ({
  nx: 0, ny: 0, nz: 0, depth: 0, px: 0, py: 0, pz: 0,
});

/** Triangle vertices in the box's local frame, reused across calls. */
const _v = new Float64Array(9);

/**
 * Overlap and contact between an oriented box and one triangle of the soup.
 *
 * OVERLAP is the full 13-axis separating-axis test (Akenine-Möller): the three
 * box axes, the triangle's face normal, and the nine edge-edge cross products.
 * The whole test is done in the BOX's local frame, where the box is the AABB
 * [-h, h] and the box axes are the basis vectors — that turns nine cross
 * products into sign flips and is why this is cheap enough to run per triangle.
 *
 * RESOLUTION is always along the triangle's FACE NORMAL, never along the
 * minimum-overlap axis that the SAT also gives you. That is deliberate. A
 * minimum-translation resolver against a triangle SOUP hits the classic
 * internal-edge artefact: the arena floor is hundreds of coplanar triangles, and
 * a box sliding across one gets pushed sideways by the shared edge's axis as if
 * the edge were a step. Resolving along the face normal cannot do that, and it
 * is exactly right for a closed shell whose normals all point into the field.
 *
 * The CONTACT POINT is the centroid of the box's support set in the -normal
 * direction: the deepest vertex when the box meets the surface on a corner, the
 * midpoint of the deepest edge when on an edge, and the centre of the deepest
 * FACE when the box is flat against the surface. That last case is the one that
 * matters — using "the deepest vertex" there would pick an arbitrary corner of a
 * flat landing and spin the car about it. A zero component of the normal is what
 * distinguishes the three cases, so it falls out of one expression.
 *
 * Returns false when there is no overlap, in which case `out` is untouched.
 */
export const obbTriangleContactFlat = (
  cx: number, cy: number, cz: number,
  m: Mat3,
  hx: number, hy: number, hz: number,
  tris: Float64Array, o: number,
  normals: Float64Array, no: number,
  out: OBBTriangleHit
): boolean => {
  const nx = normals[no];
  const ny = normals[no + 1];
  const nz = normals[no + 2];
  if (nx === 0 && ny === 0 && nz === 0) return false; // degenerate sliver

  // THE PLANE TEST COMES FIRST and does almost all the work. Rotating the
  // normal into the box frame is nine multiplies; transforming all three
  // vertices is twenty-seven, and the full SAT is an order of magnitude more
  // again. Nearly every candidate the BVH hands over is a triangle whose plane
  // the box does not even reach — a car at ride height clears the floor plane
  // by 18 uu against a box radius of 19 — so this line is the difference
  // between the body pass being free and it being the most expensive thing in
  // the tick.
  const nlx = m[0] * nx + m[3] * ny + m[6] * nz;
  const nly = m[1] * nx + m[4] * ny + m[7] * nz;
  const nlz = m[2] * nx + m[5] * ny + m[8] * nz;
  const radius = hx * Math.abs(nlx) + hy * Math.abs(nly) + hz * Math.abs(nlz);

  const ax = tris[o];
  const ay = tris[o + 1];
  const az = tris[o + 2];
  // Signed distance from the box centre to the triangle's plane, positive on
  // the side the normal points to (into the field, as the mesh is wound).
  const centreDist = (cx - ax) * nx + (cy - ay) * ny + (cz - az) * nz;
  if (centreDist > radius || centreDist < -radius) return false;

  // Triangle into the box's local frame: v = M^T * (p - centre).
  for (let i = 0; i < 3; i++) {
    const dx = tris[o + i * 3] - cx;
    const dy = tris[o + i * 3 + 1] - cy;
    const dz = tris[o + i * 3 + 2] - cz;
    _v[i * 3] = m[0] * dx + m[3] * dy + m[6] * dz;
    _v[i * 3 + 1] = m[1] * dx + m[4] * dy + m[7] * dz;
    _v[i * 3 + 2] = m[2] * dx + m[5] * dy + m[8] * dz;
  }

  const v0x = _v[0], v0y = _v[1], v0z = _v[2];
  const v1x = _v[3], v1y = _v[4], v1z = _v[5];
  const v2x = _v[6], v2y = _v[7], v2z = _v[8];

  // --- the three box axes
  if (Math.min(v0x, v1x, v2x) > hx || Math.max(v0x, v1x, v2x) < -hx) return false;
  if (Math.min(v0y, v1y, v2y) > hy || Math.max(v0y, v1y, v2y) < -hy) return false;
  if (Math.min(v0z, v1z, v2z) > hz || Math.max(v0z, v1z, v2z) < -hz) return false;

  const f0x = v1x - v0x, f0y = v1y - v0y, f0z = v1z - v0z;
  const f1x = v2x - v1x, f1y = v2y - v1y, f1z = v2z - v1z;
  const f2x = v0x - v2x, f2y = v0y - v2y, f2z = v0z - v2z;

  // --- the nine edge-edge axes, cross(boxAxis_i, triangleEdge_j). In the local
  // frame the box axes are (1,0,0), (0,1,0), (0,0,1), so each cross product is
  // a permutation with a sign flip and needs no multiplies.
  if (!crossAxes(v0y, v0z, v1y, v1z, v2y, v2z, f0y, f0z, f1y, f1z, f2y, f2z, hy, hz)) return false;
  if (!crossAxes(v0z, v0x, v1z, v1x, v2z, v2x, f0z, f0x, f1z, f1x, f2z, f2x, hz, hx)) return false;
  if (!crossAxes(v0x, v0y, v1x, v1y, v2x, v2y, f0x, f0y, f1x, f1y, f2x, f2y, hx, hy)) return false;

  // Support centroid in the -normal direction; an exactly zero component means
  // the box is flat on that axis and the support set is a whole face or edge.
  const sx = nlx > 0 ? -hx : nlx < 0 ? hx : 0;
  const sy = nly > 0 ? -hy : nly < 0 ? hy : 0;
  const sz = nlz > 0 ? -hz : nlz < 0 ? hz : 0;

  out.nx = nx;
  out.ny = ny;
  out.nz = nz;
  out.depth = radius - centreDist;
  out.px = cx + m[0] * sx + m[1] * sy + m[2] * sz;
  out.py = cy + m[3] * sx + m[4] * sy + m[5] * sz;
  out.pz = cz + m[6] * sx + m[7] * sy + m[8] * sz;
  return true;
};

/**
 * The three edge-edge axes for one box axis, written once and called three
 * times with the coordinates cycled. `(p, q)` are the two components that
 * survive the cross product with the box axis; the third is always zero.
 * Returns false as soon as one of the three separates.
 */
const crossAxes = (
  v0p: number, v0q: number,
  v1p: number, v1q: number,
  v2p: number, v2q: number,
  f0p: number, f0q: number,
  f1p: number, f1q: number,
  f2p: number, f2q: number,
  hp: number, hq: number
): boolean => {
  // cross(axis, f) has components (0, -f.q, f.p) in the cycled frame, so
  // dot(v, cross) = v.q * f.p - v.p * f.q.
  let a0 = v0q * f0p - v0p * f0q;
  let a1 = v1q * f0p - v1p * f0q;
  let a2 = v2q * f0p - v2p * f0q;
  let r = hp * Math.abs(f0q) + hq * Math.abs(f0p);
  if (Math.min(a0, a1, a2) > r || Math.max(a0, a1, a2) < -r) return false;

  a0 = v0q * f1p - v0p * f1q;
  a1 = v1q * f1p - v1p * f1q;
  a2 = v2q * f1p - v2p * f1q;
  r = hp * Math.abs(f1q) + hq * Math.abs(f1p);
  if (Math.min(a0, a1, a2) > r || Math.max(a0, a1, a2) < -r) return false;

  a0 = v0q * f2p - v0p * f2q;
  a1 = v1q * f2p - v1p * f2q;
  a2 = v2q * f2p - v2p * f2q;
  r = hp * Math.abs(f2q) + hq * Math.abs(f2p);
  return !(Math.min(a0, a1, a2) > r || Math.max(a0, a1, a2) < -r);
};

// ---------------------------------------------------------------------------
// Ray
// ---------------------------------------------------------------------------

export interface RayTriangleHit {
  /** Distance along `direction` (which must be a unit vector). */
  t: number;
  /** Barycentric weight of vertex b. */
  u: number;
  /** Barycentric weight of vertex c. */
  v: number;
  /** True when the ray hit the side the face normal points away from. */
  backface: boolean;
}

/**
 * Möller–Trumbore. `direction` must be normalized for `t` to be a distance.
 *
 * `det > 0` means the ray runs against the face normal, i.e. it hits the front
 * face: `det = [e1, dir, e2] = -dot(dir, cross(e3, e1))`.
 */
const RAY_EPS = 1e-12;

export const rayTriangleFlat = (
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  tris: Float64Array, o: number,
  maxT: number,
  cullBackface: boolean
): number => {
  const ax = tris[o];
  const ay = tris[o + 1];
  const az = tris[o + 2];
  const e1x = tris[o + 3] - ax;
  const e1y = tris[o + 4] - ay;
  const e1z = tris[o + 5] - az;
  const e2x = tris[o + 6] - ax;
  const e2y = tris[o + 7] - ay;
  const e2z = tris[o + 8] - az;

  // h = cross(dir, e2)
  const hx = dy * e2z - dz * e2y;
  const hy = dz * e2x - dx * e2z;
  const hz = dx * e2y - dy * e2x;
  const det = e1x * hx + e1y * hy + e1z * hz;

  if (cullBackface ? det < RAY_EPS : det > -RAY_EPS && det < RAY_EPS) return -1;

  const invDet = 1 / det;
  const sx = ox - ax;
  const sy = oy - ay;
  const sz = oz - az;
  const u = (sx * hx + sy * hy + sz * hz) * invDet;
  if (u < 0 || u > 1) return -1;

  // q = cross(s, e1)
  const qx = sy * e1z - sz * e1y;
  const qy = sz * e1x - sx * e1z;
  const qz = sx * e1y - sy * e1x;
  const v = (dx * qx + dy * qy + dz * qz) * invDet;
  if (v < 0 || u + v > 1) return -1;

  // Divide rather than multiply by invDet: for axis-aligned walls this makes
  // the returned distance exactly the wall coordinate instead of 1 ulp off.
  const t = (e2x * qx + e2y * qy + e2z * qz) / det;
  if (t < 0 || t > maxT) return -1;
  return t;
};

export const rayTriangle = (
  origin: Vec3,
  direction: Vec3,
  a: Vec3,
  b: Vec3,
  c: Vec3,
  maxT = Infinity,
  cullBackface = false
): RayTriangleHit | null => {
  loadTri(a, b, c);
  const t = rayTriangleFlat(
    origin.x, origin.y, origin.z,
    direction.x, direction.y, direction.z,
    _tri, 0, maxT, cullBackface
  );
  if (t < 0) return null;

  // Recover u, v and the facing for the reporting API. Cheap: only on a hit.
  const e1 = sub(b, a);
  const e2 = sub(c, a);
  const h = cross(direction, e2);
  const det = e1.x * h.x + e1.y * h.y + e1.z * h.z;
  const s = sub(origin, a);
  const u = (s.x * h.x + s.y * h.y + s.z * h.z) / det;
  const q = cross(s, e1);
  const v = (direction.x * q.x + direction.y * q.y + direction.z * q.z) / det;
  return { t, u, v, backface: det < 0 };
};
